import React, { useState, useEffect, lazy, Suspense, memo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  Card,
  CardContent,
  CardActions,
  LinearProgress,
  useTheme,
  useMediaQuery,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  alpha,
  Skeleton,
} from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import PieChartIcon from '@mui/icons-material/PieChart';
import DescriptionIcon from '@mui/icons-material/Description';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BarChartIcon from '@mui/icons-material/BarChart';
import { StatusChipProps, Project } from '../types';
import { projectAPI } from '../apis';
import { isAuthenticated } from '../utils/auth';
import { grey } from '@mui/material/colors';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, PieChart, Pie, Sector } from 'recharts';

const MetricCard = memo(styled(Card)(({ theme }: { theme: Theme }) => ({
  height: '140px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  borderRadius: '16px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  background: theme.palette.background.paper,
})));

const MetricValue = memo(styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: '60px',
  fontWeight: 700,
  lineHeight: 1,
  marginBottom: '12px',
  color: theme.palette.text.primary,
})));

const StyledTable = styled(Table)({
  '& .MuiTableCell-root': {
    borderBottom: '1px solid #f0f0f0',
    padding: '16px',
  },
  '& .MuiTableCell-head': {
    fontWeight: 600,
    backgroundColor: '#f8f9fa',
  },
});

const StatusChip = styled(Chip)<StatusChipProps>(({ status, theme }) => {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'in_progress':
        return {
          bg: '#FFF6DE',
          color: '#E5A206',
          border: '#F5D679',
        };
      case 'completed':
        return {
          bg: '#E8F5E9',
          color: '#2E7D32',
          border: '#A5D6A7',
        };
      case 'pending':
        return {
          bg: '#E3F2FD',
          color: '#1976D2',
          border: '#90CAF9',
        };
      default:
        return {
          bg: theme.palette.grey[100],
          color: theme.palette.grey[700],
          border: theme.palette.grey[300],
        };
    }
  };

  const colors = getStatusColor(status);
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    border: `1px solid ${colors.border}`,
    fontWeight: 600,
    borderRadius: '20px',
    padding: '4px 12px',
  };
});

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    transform: 'scale(1.001)',
  },
}));

const FilterTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  minWidth: 'auto',
  padding: '12px 16px',
  borderRadius: '8px',
  margin: '0 4px',
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
  },
}));

const DashboardSkeleton = () => {
  const theme = useTheme();
  
  return (
    <Box sx={{ 
      pt: '80px',
      bgcolor: '#F8F9FA', 
      minHeight: '100vh',
      pb: 4,
    }}>
      <Container maxWidth="lg">
        {/* Metrics Cards - Removed */}
        {/* <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3].map((index) => (
            <Grid item xs={12} md={4} key={index}>
              <MetricCard>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Skeleton variant="text" width={120} height={24} />
                    <Skeleton variant="text" width={80} height={60} sx={{ my: 1 }} />
                  </Box>
                  <Skeleton variant="circular" width={40} height={40} />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <Skeleton variant="text" width="60%" height={16} />
                </Box>
              </MetricCard>
            </Grid>
          ))}
        </Grid> */}

        {/* Chart Skeletons - Side by Side */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Chart Skeleton 1 */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ borderRadius: '16px', p: 2, pb: 1, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Skeleton variant="text" width={150} height={32} />
                <Skeleton variant="circular" width={40} height={40} />
              </Box>
              <Skeleton variant="rectangular" width="100%" height={260} sx={{ borderRadius: 1 }} />
            </Paper>
          </Grid>

          {/* Chart Skeleton 2 */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ borderRadius: '16px', p: 2, pb: 1, overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Skeleton variant="text" width={150} height={32} />
                <Skeleton variant="circular" width={40} height={40} />
              </Box>
              <Skeleton variant="rectangular" width="100%" height={260} sx={{ borderRadius: 1 }} />
            </Paper>
          </Grid>
        </Grid>

        {/* Filter Tabs */}
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="rectangular" width={300} height={40} sx={{ borderRadius: 2 }} />
        </Box>

        {/* Projects Table */}
        <Paper sx={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)' }}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Skeleton variant="text" width={200} height={32} />
              <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: '30px' }} />
            </Box>

            <Table>
              <TableHead>
                <TableRow>
                  {['Project Name', 'Type', 'Status', 'Progress', 'Actions'].map((header, index) => (
                    <TableCell key={index}>
                      <Skeleton variant="text" width="80%" height={24} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[1, 2, 3, 4].map((row) => (
                  <TableRow key={row}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Box>
                          <Skeleton variant="text" width={150} height={24} />
                          <Skeleton variant="text" width={100} height={16} />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={100} height={24} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: '20px' }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="rectangular" width={120} height={8} sx={{ borderRadius: 1 }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="circular" width={32} height={32} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filter, setFilter] = useState('all');
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState({
    activeProjects: 0,
    pendingReviews: 0,
    projectDocuments: 0,
  });
  const [projectTypeData, setProjectTypeData] = useState<Array<{type: string, count: number}>>([]);
  const [projectStatusData, setProjectStatusData] = useState<Array<{name: string, value: number, originalStatus: string}>>([]);

  // Define chart colors - a palette that works well together
  const chartColors = [
    '#4169E1', // Royal Blue
    '#FF6B6B', // Coral Red
    '#48C9B0', // Teal
    '#FFD700', // Gold
    '#9B59B6', // Amethyst
    '#F39C12', // Orange
    '#1ABC9C', // Turquoise
    '#E74C3C', // Crimson
    '#3498DB', // Dodger Blue
    '#2ECC71', // Emerald
  ];

  // Status-specific colors
  const statusColors = {
    pending: '#3498DB',    // Blue
    in_progress: '#F39C12', // Orange
    completed: '#2ECC71',   // Green
    draft: '#95A5A6',       // Gray
  };

  // Custom tooltip component for improved appearance
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 2,
            borderRadius: 1,
            boxShadow: '0 3px 14px rgba(0,0,0,0.15)',
            border: '1px solid #f0f0f0',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <span style={{ color: payload[0].color, fontWeight: 600 }}>
              {payload[0].value}
            </span> projects
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Custom tooltip for pie chart
  const PieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      // Calculate the percentage manually based on the value and the total
      const value = payload[0].value || 0;
      const totalValue = projectStatusData.reduce((sum, item) => sum + item.value, 0);
      const percent = totalValue > 0 ? (value / totalValue) * 100 : 0;
      
      return (
        <Box
          sx={{
            bgcolor: 'background.paper',
            p: 2,
            borderRadius: 1,
            boxShadow: '0 3px 14px rgba(0,0,0,0.15)',
            border: '1px solid #f0f0f0',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {payload[0].name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <span style={{ color: payload[0].fill, fontWeight: 600 }}>
              {value}
            </span> projects ({percent.toFixed(0)}%)
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Active pie sector renderer
  const renderActiveShape = (props: any) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload, percent, value } = props;
  
    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 8}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
      </g>
    );
  };
  
  const [activeIndex, setActiveIndex] = useState(0);
  
  const onPieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!isAuthenticated()) {
          navigate('/login', { state: { from: '/dashboard' } });
          return;
        }

        const response = await projectAPI.getProjects();
        
        if (response.status === 'success' && response.data) {
          setProjects(response.data);
          
          // Calculate metrics from project data
          const activeCount = response.data.filter(p => p.status === 'in_progress').length;
          const pendingCount = response.data.filter(p => p.status === 'pending').length;
          const totalProjects = response.data.length;
          
          setMetrics({
            activeProjects: activeCount,
            pendingReviews: pendingCount,
            projectDocuments: totalProjects,
          });

          // Process project type data for chart
          const projectTypes: Record<string, number> = {};
          response.data.forEach(project => {
            const type = project.projectType || 'Unknown';
            projectTypes[type] = (projectTypes[type] || 0) + 1;
          });

          const chartData = Object.entries(projectTypes).map(([type, count]) => ({
            type,
            count
          }));

          setProjectTypeData(chartData);
          
          // Process project status data for pie chart
          const statuses: Record<string, number> = {};
          response.data.forEach(project => {
            const status = project.status || 'Unknown';
            statuses[status] = (statuses[status] || 0) + 1;
          });
          
          const statusData = Object.entries(statuses).map(([name, value]) => ({
            name: name === 'in_progress' ? 'In Progress' : 
                 name === 'completed' ? 'Completed' : 
                 name.charAt(0).toUpperCase() + name.slice(1),
            value,
            originalStatus: name
          }));
          
          setProjectStatusData(statusData);
        } else {
          throw new Error(response.message || 'Failed to fetch projects');
        }
      } catch (error: any) {
        console.error('Error fetching projects:', error);
        if (error.status === 401) {
          navigate('/login', { state: { from: '/dashboard' } });
        } else {
          setError('Failed to load projects. Please try again later.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [navigate]);

  const metricCards = [
    { 
      title: 'Active Project', 
      value: metrics.projectDocuments.toString(), 
      icon: <PieChartIcon sx={{ fontSize: 40, color: '#4169E1' }} /> 
    },
    { 
      title: 'Pending Approvals', 
      value: metrics.pendingReviews.toString(), 
      icon: <ChecklistIcon sx={{ fontSize: 40, color: '#7B7D8C' }} /> 
    },
    { 
      title: 'Queued Documents', 
      value: metrics.activeProjects.toString(), 
      icon: <DescriptionIcon sx={{ fontSize: 40, color: '#2A2C40' }} /> 
    },
  ];

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(project => {
        if (filter === 'in_progress') {
          return project.status === 'in_progress' || project.status === 'pending';
        }
        return project.status.toLowerCase().replace(' ', '_') === filter;
      });

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <Suspense fallback={<CircularProgress />}>
      <Box sx={{ 
        pt: '80px',
        bgcolor: '#F8F9FA', 
        minHeight: '100vh',
        pb: 4,
      }}>
        <Container maxWidth="lg">
          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}
            >
              {error}
            </Alert>
          )}

          {/* Removed Metric Cards Section */}
          {/* <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)' 
            }, 
            gap: 3, 
            mb: 4 
          }}>
            {metricCards.map((metric) => (
              <MetricCard key={metric.title}>
                <Typography color="text.secondary" sx={{ fontSize: '1rem', fontWeight: 500 }}>
                  {metric.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <MetricValue>
                    {metric.value}
                  </MetricValue>
                  <Box sx={{
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                    {metric.icon}
                  </Box>
                </Box>
              </MetricCard>
            ))}
          </Box> */}

          {/* Charts Section - Side by Side */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Project Type Bar Chart */}
            <Grid item xs={12} md={6}>
              <Paper 
                sx={{ 
                  borderRadius: '16px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                  p: 2,
                  pb: 1,
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2,
                }}>
                  <Typography variant="h5" fontWeight="600">
                    Plan Types
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  }}>
                    <BarChartIcon sx={{ color: theme.palette.primary.main }} />
                  </Box>
                </Box>

                <Box sx={{ width: '100%', height: 260 }}>
                  {projectTypeData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={projectTypeData}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 30,
                        }}
                        barSize={60}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis 
                          dataKey="type" 
                          angle={-45} 
                          textAnchor="end" 
                          height={70} 
                          tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                          tickLine={false}
                          axisLine={{ stroke: '#E0E0E0' }}
                        />
                        <YAxis 
                          tickLine={false}
                          axisLine={{ stroke: '#E0E0E0' }}
                          tick={{ fill: theme.palette.text.secondary }}
                        />
                        <Tooltip content={<CustomTooltip />} cursor={{ fill: alpha(theme.palette.primary.main, 0.05) }} />
                        <Bar 
                          dataKey="count" 
                          name=" " 
                          radius={[4, 4, 0, 0]}
                        >
                          {projectTypeData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={chartColors[index % chartColors.length]} 
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      height: '100%', 
                      justifyContent: 'center', 
                      alignItems: 'center' 
                    }}>
                      <Typography variant="body1" color="text.secondary">
                        No project type data available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>

            {/* Project Status Pie Chart */}
            <Grid item xs={12} md={6}>
              <Paper 
                sx={{ 
                  borderRadius: '16px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                  p: 2,
                  pb: 1,
                  overflow: 'hidden'
                }}
              >
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 2,
                }}>
                  <Typography variant="h5" fontWeight="600">
                    Plan Status
                  </Typography>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '12px',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  }}>
                    <PieChartIcon sx={{ color: theme.palette.primary.main }} />
                  </Box>
                </Box>

                <Box sx={{ width: '100%', height: 260, display: 'flex', justifyContent: 'center' }}>
                  {projectStatusData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          activeIndex={activeIndex}
                          activeShape={renderActiveShape}
                          data={projectStatusData}
                          cx="50%"
                          cy="50%"
                          innerRadius={80}
                          outerRadius={100}
                          paddingAngle={3}
                          dataKey="value"
                          onMouseEnter={onPieEnter}
                          nameKey="name"
                          isAnimationActive={true}
                          animationBegin={200}
                          animationDuration={800}
                        >
                          {projectStatusData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={statusColors[entry.originalStatus as keyof typeof statusColors] || chartColors[index % chartColors.length]} 
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                        <Legend 
                          iconType="circle"
                          layout="horizontal"
                          verticalAlign="bottom"
                          align="center"
                          formatter={(value, entry, index) => (
                            <span style={{ color: theme.palette.text.primary, fontSize: '0.875rem' }}>
                              {value}
                            </span>
                          )}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ 
                      display: 'flex', 
                      height: '100%', 
                      justifyContent: 'center', 
                      alignItems: 'center' 
                    }}>
                      <Typography variant="body1" color="text.secondary">
                        No project status data available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mb: 4 }}>
            <Paper 
              sx={{ 
                borderRadius: '16px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                overflow: 'hidden'
              }}
            >
              <Box sx={{ 
                p: 3,
                borderBottom: '1px solid #f0f0f0',
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 3,
                }}>
                  <Typography variant="h5" fontWeight="600">Projects</Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/new-project')}
                    sx={{
                      borderRadius: '30px',
                      textTransform: 'none',
                      px: 3,
                      py: 1.2,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      bgcolor: '#4169E1',
                      '&:hover': {
                        bgcolor: '#3457cd',
                      },
                      transition: 'all 0.2s ease',
                      fontWeight: 600,
                    }}
                  >
                    Create New Project
                  </Button>
                </Box>

                <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '30px', display: 'inline-flex', p: '4px' }}>
                  <Button 
                    variant={filter === 'all' ? 'contained' : 'text'} 
                    onClick={() => setFilter('all')}
                    sx={{ 
                      borderRadius: '30px', 
                      textTransform: 'none', 
                      px: 3,
                      py: 1,
                      mr: 1,
                      bgcolor: filter === 'all' ? '#2A2C40' : '#F2F2F2',
                      color: filter === 'all' ? '#fff' : '#2A2C40',
                      '&:hover': {
                        bgcolor: filter === 'all' ? '#2A2C40' : '#E5E5E5',
                      },
                      fontWeight: 600,
                    }}
                  >
                    All
                  </Button>
                  <Button 
                    variant={filter === 'in_progress' ? 'contained' : 'text'} 
                    onClick={() => setFilter('in_progress')}
                    sx={{ 
                      borderRadius: '30px', 
                      textTransform: 'none', 
                      px: 3,
                      py: 1,
                      mr: 1,
                      bgcolor: filter === 'in_progress' ? '#2A2C40' : '#F2F2F2',
                      color: filter === 'in_progress' ? '#fff' : '#2A2C40',
                      '&:hover': {
                        bgcolor: filter === 'in_progress' ? '#2A2C40' : '#E5E5E5',
                      },
                      fontWeight: 600,
                    }}
                  >
                    Active & Pending
                  </Button>
                  <Button 
                    variant={filter === 'completed' ? 'contained' : 'text'} 
                    onClick={() => setFilter('completed')}
                    sx={{ 
                      borderRadius: '30px', 
                      textTransform: 'none', 
                      px: 3,
                      py: 1,
                      mr: 1,
                      bgcolor: filter === 'completed' ? '#2A2C40' : '#F2F2F2',
                      color: filter === 'completed' ? '#fff' : '#2A2C40',
                      '&:hover': {
                        bgcolor: filter === 'completed' ? '#2A2C40' : '#E5E5E5',
                      },
                      fontWeight: 600,
                    }}
                  >
                    Completed
                  </Button>
                  <Button 
                    variant={filter === 'draft' ? 'contained' : 'text'} 
                    onClick={() => setFilter('draft')}
                    sx={{ 
                      borderRadius: '30px', 
                      textTransform: 'none', 
                      px: 3,
                      py: 1,
                      bgcolor: filter === 'draft' ? '#2A2C40' : '#F2F2F2',
                      color: filter === 'draft' ? '#fff' : '#2A2C40',
                      '&:hover': {
                        bgcolor: filter === 'draft' ? '#2A2C40' : '#E5E5E5',
                      },
                      fontWeight: 600,
                    }}
                  >
                    Draft
                  </Button>
                </Box>
              </Box>

              {filteredProjects.length === 0 ? (
                <Box 
                  sx={{ 
                    p: 6, 
                    textAlign: 'center', 
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
                  }}
                >
                  <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                    No projects found
                  </Typography>
                  <Typography variant="body1" color="text.secondary" paragraph>
                    {filter === 'all' 
                      ? 'Start by creating your first project'
                      : `No ${filter.replace('_', ' ')} projects found`}
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/new-project')}
                    sx={{
                      borderRadius: '30px',
                      textTransform: 'none',
                      px: 4,
                      py: 1.5,
                      fontSize: '1rem',
                      bgcolor: '#4169E1',
                    }}
                  >
                    Create Project
                  </Button>
                </Box>
              ) : (
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Project Name</TableCell>
                      <TableCell>Function</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last Updated</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredProjects.map((project) => (
                      <StyledTableRow 
                        key={project.projectID}
                        onClick={() => navigate(`/project/${project.projectID}`)}
                      >
                        <TableCell>
                          <Typography fontWeight={600}>{project.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography color="text.secondary">{project.projectType}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography color="text.secondary">{project.address || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell>
                          <StatusChip
                            label={project.status === 'in_progress' ? 'In Progress' : 
                                   project.status === 'completed' ? 'Complete' : 'Pending'}
                            status={project.status}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography color="text.secondary">
                            Today, 2:30 PM
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/project/${project.projectID}/edit`);
                            }}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': {
                                color: 'primary.main',
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                              },
                            }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Paper>
          </Box>
        </Container>
      </Box>
    </Suspense>
  );
};

export default memo(Dashboard); 